import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { apiGet } from "@/api/client";
import { qk } from "@/api/keys";
import { useAuthStore } from "@/stores/authStore";

export interface MeResponse {
  user_id: string;
  phone: string | null;
  email: string | null;
  display_name: string | null;
}

export function useMe() {
  const token = useAuthStore((s) => s.token);
  return useQuery({
    queryKey: qk.me(),
    queryFn: () => apiGet<MeResponse>("me"),
    enabled: Boolean(token),
  });
}

/**
 * Fetches `/api/me` and mirrors the canonical identity fields (notably
 * `display_name`) into the persisted auth store. The store otherwise only
 * learns `display_name` when the user edits it in-session, so without this a
 * fresh load / relogin falls back to the user-id slice or phone tail.
 *
 * Mount once in `AppLayout` so every authenticated page sees the synced value.
 */
export function useHydrateUserSummary(): void {
  const { data } = useMe();
  useEffect(() => {
    if (!data) return;
    const current = useAuthStore.getState().userSummary;
    if (
      current?.user_id === data.user_id &&
      current?.phone === data.phone &&
      current?.email === data.email &&
      current?.display_name === data.display_name
    ) {
      return;
    }
    useAuthStore.getState().setUserSummary({
      user_id: data.user_id,
      phone: data.phone,
      email: data.email,
      display_name: data.display_name,
    });
  }, [data]);
}
